import { Injectable } from '@angular/core';
import {
  GET_DASHBOARD_ACTIVITY_STREAK,
  GET_DASHBOARD_CONVERSION_RATE,
  GET_DASHBOARD_FOLLOW_UP_NEEDED,
  GET_DASHBOARD_LEADERBOARD,
  GET_DASHBOARD_MILESTONES,
  GET_DASHBOARD_PENDING_OFFERS,
  GET_DASHBOARD_PIPELINE_VALUE,
  GET_DASHBOARD_PROPOSALS_BY_STATUS,
  GET_DASHBOARD_SALES_TARGET,
  GET_DASHBOARD_STALE_CLIENTS,
  GET_DASHBOARD_TOP_CLIENTS,
  GET_DASHBOARD_TOP_SPECIES,
  SET_DASHBOARD_SALES_TARGET,
} from '@queries/dashboard.graphql';
import { Apollo } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { map, Observable } from 'rxjs';

import {
  ActivityStreak,
  ConversionRate,
  FollowUpRow,
  LeaderboardRow,
  Milestones,
  PendingOffers,
  PipelineValue,
  ProposalsByStatusRow,
  SalesTarget,
  SalesTargetInput,
  SalesTargetProgress,
  StaleClientRow,
  TopClientRow,
  TopSpeciesRow,
} from './dashboard.types';

/**
 * Thin, stateless Apollo wrappers — one per dashboard query (spec §2). Unlike
 * core/proposals etc. there is deliberately NO BehaviorSubject layer: dashboard data is
 * read-only and per-visit, so every call fires a fresh network request and each widget
 * owns its own subscription — and therefore its own error state (spec §0.10).
 */
@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  constructor(private readonly _apollo: Apollo) {}

  getProposalsByStatus(): Observable<ProposalsByStatusRow[]> {
    return this._watch<ProposalsByStatusRow[]>(GET_DASHBOARD_PROPOSALS_BY_STATUS, 'dashboardProposalsByStatus');
  }

  getPipelineValue(): Observable<PipelineValue> {
    return this._watch<PipelineValue>(GET_DASHBOARD_PIPELINE_VALUE, 'dashboardPipelineValue');
  }

  getConversionRate(): Observable<ConversionRate> {
    return this._watch<ConversionRate>(GET_DASHBOARD_CONVERSION_RATE, 'dashboardConversionRate');
  }

  getFollowUpNeeded(): Observable<FollowUpRow[]> {
    return this._watch<FollowUpRow[]>(GET_DASHBOARD_FOLLOW_UP_NEEDED, 'dashboardFollowUpNeeded');
  }

  getStaleClients(): Observable<StaleClientRow[]> {
    return this._watch<StaleClientRow[]>(GET_DASHBOARD_STALE_CLIENTS, 'dashboardStaleClients');
  }

  getSalesTarget(): Observable<SalesTargetProgress> {
    return this._watch<SalesTargetProgress>(GET_DASHBOARD_SALES_TARGET, 'dashboardSalesTarget');
  }

  getActivityStreak(): Observable<ActivityStreak> {
    return this._watch<ActivityStreak>(GET_DASHBOARD_ACTIVITY_STREAK, 'dashboardActivityStreak');
  }

  getMilestones(): Observable<Milestones> {
    return this._watch<Milestones>(GET_DASHBOARD_MILESTONES, 'dashboardMilestones');
  }

  getLeaderboard(): Observable<LeaderboardRow[]> {
    return this._watch<LeaderboardRow[]>(GET_DASHBOARD_LEADERBOARD, 'dashboardLeaderboard');
  }

  getPendingOffers(): Observable<PendingOffers> {
    return this._watch<PendingOffers>(GET_DASHBOARD_PENDING_OFFERS, 'dashboardPendingOffers');
  }

  getTopClients(): Observable<TopClientRow[]> {
    return this._watch<TopClientRow[]>(GET_DASHBOARD_TOP_CLIENTS, 'dashboardTopClients');
  }

  getTopSpecies(): Observable<TopSpeciesRow[]> {
    return this._watch<TopSpeciesRow[]>(GET_DASHBOARD_TOP_SPECIES, 'dashboardTopSpecies');
  }

  setSalesTarget(input: SalesTargetInput): Observable<SalesTarget> {
    return this._apollo
      .mutate<{ dashboardSalesTargetSet: SalesTarget }>({
        mutation: SET_DASHBOARD_SALES_TARGET,
        variables: {
          userId: input.userId,
          year: input.year,
          month: input.month,
          targetValue: input.targetValue,
        },
      })
      .pipe(map(({ data }) => data.dashboardSalesTargetSet));
  }

  /** network-only: every call is a real refetch (per-visit data, and "Tentar novamente" must hit the server). */
  private _watch<T>(query: DocumentNode, field: string): Observable<T> {
    return this._apollo
      .watchQuery<Record<string, T>>({ query, fetchPolicy: 'network-only' })
      .valueChanges.pipe(map(({ data }) => data[field]));
  }
}
